import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Heart } from 'lucide-react'
import useAuthStore from '../../store/useAuthStore'
import client from '../../api/client'
import AdCarousel from '../ui/AdCarousel'
import { useQuery } from '@tanstack/react-query'

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const [ads, setAds] = useState([])

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos || {}
    },
    staleTime: 5 * 60 * 1000,
  })

  const nombreSistema = settings?.nombre_sistema || 'SubiGol'
  const logoSistema = settings?.logo_sistema || '/favicon.svg'
  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')
  const logoUrl = logoSistema.startsWith('/uploads') ? `${baseImgUrl}${logoSistema}` : (logoSistema.startsWith('/') ? `${window.__APP_BASE_URL__ || '/'}${logoSistema.substring(1)}` : logoSistema)

  useEffect(() => {
    if (isAuthenticated) {
      client.get('/ads?ubicacion=footer')
        .then(res => {
          setAds(res.data?.datos || [])
        })
        .catch(err => {
          console.error('Error fetching footer ads:', err)
        })
    }
  }, [isAuthenticated])

  return (
    <footer className="border-t border-border-subtle bg-navy/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {!isAuthenticated && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Columna de marca */}
            <div className="sm:col-span-2 lg:col-span-1">
              <Link to="/" className="flex items-center gap-2.5 mb-3 group inline-flex">
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="w-8 h-8 object-contain transition-transform duration-700 ease-out group-hover:rotate-[360deg] group-hover:scale-110"
                />
                <span className="font-display font-bold text-lg text-text-primary">
                  {nombreSistema}
                </span>
              </Link>
              <p className="text-sm text-text-muted leading-relaxed">
                La plataforma definitiva para predecir el Mundial 2026. 
                Compite con amigos y demuestra quién sabe más de fútbol.
              </p>
            </div>

            {/* Explorar */}
            <div>
              <h4 className="font-display font-semibold text-sm text-text-primary mb-3">Explorar</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-sm text-text-muted hover:text-cyan transition-colors">Inicio</Link></li>
                <li><Link to="/partidos" className="text-sm text-text-muted hover:text-cyan transition-colors">Partidos</Link></li>
                <li><Link to="/clasificacion" className="text-sm text-text-muted hover:text-cyan transition-colors">Clasificaciones</Link></li>
              </ul>
            </div>

            {/* Cuenta */}
            <div>
              <h4 className="font-display font-semibold text-sm text-text-primary mb-3">Cuenta</h4>
              <ul className="space-y-2">
                <li><Link to="/login" className="text-sm text-text-muted hover:text-cyan transition-colors">Iniciar sesión</Link></li>
                <li><Link to="/registro" className="text-sm text-text-muted hover:text-cyan transition-colors">Crear cuenta</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-display font-semibold text-sm text-text-primary mb-3">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/reglas" className="text-sm text-text-muted hover:text-cyan transition-colors">Reglas del juego</Link></li>
                <li><Link to="/terminos" className="text-sm text-text-muted hover:text-cyan transition-colors">Términos de uso</Link></li>
                <li><Link to="/privacidad" className="text-sm text-text-muted hover:text-cyan transition-colors">Privacidad</Link></li>
                <li><Link to="/dmca" className="text-sm text-text-muted hover:text-cyan transition-colors">DMCA y Terceros</Link></li>
              </ul>
            </div>
          </div>
        )}

        {/* Carrusel de Auspiciadores Compacto */}
        {isAuthenticated && ads.length > 0 && (
          <div className="mb-4">
            <AdCarousel ads={ads} variant="compact" />
          </div>
        )}

        {/* Línea inferior con copyright */}
        <div className={`${!isAuthenticated ? 'pt-6 border-t border-border-subtle' : ''} flex flex-col sm:flex-row items-center justify-between gap-4`}>
          <p className="text-xs text-text-muted">
            © {currentYear} {nombreSistema}. Todos los derechos reservados.
          </p>
          <p className="text-xs text-text-muted flex items-center gap-1">
            Hecho con <Heart className="w-3 h-3 text-error fill-error" /> para fanáticos del fútbol
          </p>
        </div>
      </div>
    </footer>
  )
}
